export interface CityLicense {
  name: string;
  estimatedCost: string;
  processingTime: string;
  url: string;
  warning?: string;
}

export interface CityData {
  name: string;
  emoji: string;
  state?: string;
  country: string;
  costMultiplier: number;
  currency: string;
  currencySymbol: string;
  comingSoon?: boolean;
  licenses: Record<string, CityLicense[]>; // keyed by business type category
  warnings?: string[];
}

export interface CountryData {
  region: string;
  country: string;
  flag: string;
  cities: CityData[];
  placeholder: string; // e.g. "e.g. Miami, New York"
}

// Business types that serve alcohol
export const ALCOHOL_BUSINESS_TYPES = [
  "restaurant", "bar", "hotel", "boutique-hotel",
];

// License categories per business type
function generalLicenses(city: string): CityLicense[] {
  return [
    { name: "Business License / Tax Receipt", estimatedCost: "$50–$500", processingTime: "1–4 weeks", url: getLicenseUrl(city, "business") },
    { name: "Food Service / Health Permit", estimatedCost: "$100–$1,000", processingTime: "2–8 weeks", url: getLicenseUrl(city, "health") },
    { name: "Fire Safety / Occupancy Permit", estimatedCost: "$100–$500", processingTime: "2–6 weeks", url: getLicenseUrl(city, "fire") },
    { name: "Signage Permit", estimatedCost: "$50–$300", processingTime: "1–4 weeks", url: getLicenseUrl(city, "signage") },
  ];
}

function getLicenseUrl(city: string, type: string): string {
  const urls: Record<string, Record<string, string>> = {
    "New York": {
      business: "https://www1.nyc.gov/nycbusiness/description/general-vendor-license",
      health: "https://www1.nyc.gov/site/doh/business/food-operators/food-service-establishment-702702.page",
      fire: "https://www1.nyc.gov/site/fdny/business/all-certifications/all-certifications.page",
      signage: "https://www1.nyc.gov/site/buildings/business/sign-application-jobs.page",
      liquor: "https://sla.ny.gov/apply-for-a-license",
      beer_wine: "https://sla.ny.gov/apply-for-a-license",
    },
    "Miami": {
      business: "https://www.miamigov.com/My-Government/Departments/Finance/Local-Business-Tax-Receipt",
      health: "https://www.floridahealth.gov/environmental-health/food-safety-and-sanitation/index.html",
      fire: "https://www.miamigov.com/My-Government/Departments/Fire-Rescue",
      signage: "https://www.miamigov.com/My-Government/Departments/Building",
      liquor: "https://www.myfloridalicense.com/intentions2.asp?chession=&catid=&divid=&id=0&type=2&SID=",
      beer_wine: "https://www.myfloridalicense.com/intentions2.asp?chession=&catid=&divid=&id=0&type=2&SID=",
    },
    "Los Angeles": {
      business: "https://finance.lacity.org/business-tax",
      health: "https://publichealth.lacounty.gov/eh/business/food-facility-plan-check.htm",
      fire: "https://www.lafd.org/fire-prevention/permits",
      signage: "https://www.ladbs.org/services/check-status/sign-permits",
      liquor: "https://www.abc.ca.gov/licensing/",
      beer_wine: "https://www.abc.ca.gov/licensing/",
    },
    "Austin": {
      business: "https://www.austintexas.gov/financeonline",
      health: "https://www.austintexas.gov/department/food-establishments",
      fire: "https://www.austintexas.gov/department/fire-prevention",
      signage: "https://www.austintexas.gov/department/sign-review",
      liquor: "https://www.tabc.texas.gov/licensing/",
      beer_wine: "https://www.tabc.texas.gov/licensing/",
    },
    "Chicago": {
      business: "https://www.chicago.gov/city/en/depts/bacp/provdrs/lic.html",
      health: "https://www.chicago.gov/city/en/depts/cdph/provdrs/food_protection.html",
      fire: "https://www.chicago.gov/city/en/depts/bldgs.html",
      signage: "https://www.chicago.gov/city/en/depts/bldgs.html",
      liquor: "https://www.chicago.gov/city/en/depts/bacp/provdrs/liquor.html",
      beer_wine: "https://www.chicago.gov/city/en/depts/bacp/provdrs/liquor.html",
    },
    "London": {
      business: "https://www.gov.uk/set-up-business",
      health: "https://www.food.gov.uk/business-guidance/register-a-food-business",
      fire: "https://www.london-fire.gov.uk/safety/",
      signage: "https://www.planningportal.co.uk/",
      liquor: "https://www.gov.uk/premises-licence",
      beer_wine: "https://www.gov.uk/premises-licence",
    },
    "Paris": {
      business: "https://www.service-public.fr/professionnels-entreprises/vosdroits/F22388",
      health: "https://www.service-public.fr/professionnels-entreprises/vosdroits/F22379",
      fire: "https://www.service-public.fr/professionnels-entreprises/vosdroits/F31684",
      signage: "https://www.service-public.fr/",
      liquor: "https://www.service-public.fr/professionnels-entreprises/vosdroits/F22379",
      beer_wine: "https://www.service-public.fr/professionnels-entreprises/vosdroits/F22379",
    },
    "Dubai": {
      business: "https://www.dubaided.gov.ae/en/services",
      health: "https://www.dm.gov.ae/food-safety/",
      fire: "https://www.dubaicd.gov.ae/en",
      signage: "https://www.dm.gov.ae/",
      liquor: "https://www.dubaided.gov.ae/en/services",
      beer_wine: "https://www.dubaided.gov.ae/en/services",
    },
    "Tokyo": {
      business: "https://www.meti.go.jp/english/",
      health: "https://www.fukushihoken.metro.tokyo.lg.jp/English/",
      fire: "https://www.tfd.metro.tokyo.lg.jp/eng/",
      signage: "https://www.toshiseibi.metro.tokyo.lg.jp/",
      liquor: "https://www.nta.go.jp/english/",
      beer_wine: "https://www.nta.go.jp/english/",
    },
    "Singapore": {
      business: "https://www.acra.gov.sg/",
      health: "https://www.sfa.gov.sg/food-retail/overview",
      fire: "https://www.scdf.gov.sg/home/fire-safety/",
      signage: "https://www.ura.gov.sg/",
      liquor: "https://www.police.gov.sg/e-Services/apply/licenses-and-permits",
      beer_wine: "https://www.police.gov.sg/e-Services/apply/licenses-and-permits",
    },
  };

  const cityUrls = urls[city];
  if (cityUrls) {
    return Object.keys(cityUrls).length > 0 ? cityUrls[type] || "#" : "#";
  }
  return "#";
}

function getCityLicenses(cityName: string): Record<string, CityLicense[]> {
  const base = generalLicenses(cityName);

  const alcoholLicenses: Record<string, CityLicense[]> = {
    "Miami": [
      { name: "Full Liquor License (Quota)", estimatedCost: "$400,000–$800,000", processingTime: "3–12 months", url: getLicenseUrl("Miami", "liquor"), warning: "Quota licenses are limited and traded on secondary market. Prices vary greatly." },
      { name: "SRX Restaurant Liquor License", estimatedCost: "$1,820/yr", processingTime: "60–90 days", url: getLicenseUrl("Miami", "liquor"), warning: "Must derive 51%+ revenue from food. Cannot operate as a bar." },
      { name: "Beer & Wine License (2COP)", estimatedCost: "$1,820/yr", processingTime: "30–60 days", url: getLicenseUrl("Miami", "beer_wine") },
    ],
    "New York": [
      { name: "Full Liquor License (On-Premises)", estimatedCost: "$4,352", processingTime: "3–6 months", url: getLicenseUrl("New York", "liquor"), warning: "NYC has a 500-foot rule restricting new licenses near schools and churches." },
      { name: "Beer & Wine License", estimatedCost: "$1,103", processingTime: "2–4 months", url: getLicenseUrl("New York", "beer_wine") },
    ],
    "Los Angeles": [
      { name: "Type 47 Full Liquor (Restaurant)", estimatedCost: "$13,800", processingTime: "3–9 months", url: getLicenseUrl("Los Angeles", "liquor") },
      { name: "Type 41 Beer & Wine (Restaurant)", estimatedCost: "$1,025", processingTime: "2–6 months", url: getLicenseUrl("Los Angeles", "beer_wine") },
    ],
    "Austin": [
      { name: "Mixed Beverage License", estimatedCost: "$6,061", processingTime: "45–90 days", url: getLicenseUrl("Austin", "liquor") },
      { name: "Beer & Wine License", estimatedCost: "$1,525", processingTime: "30–60 days", url: getLicenseUrl("Austin", "beer_wine") },
    ],
    "Chicago": [
      { name: "Tavern/Liquor License", estimatedCost: "$4,400/yr", processingTime: "4–8 weeks", url: getLicenseUrl("Chicago", "liquor") },
      { name: "Package Goods License (Off-Premises)", estimatedCost: "$4,400/yr", processingTime: "4–8 weeks", url: getLicenseUrl("Chicago", "beer_wine") },
    ],
    "London": [
      { name: "Premises Licence (Alcohol)", estimatedCost: "£100–£1,905", processingTime: "4–8 weeks", url: getLicenseUrl("London", "liquor"), warning: "Requires a Designated Premises Supervisor with a personal licence." },
      { name: "Personal Licence", estimatedCost: "£37", processingTime: "2–4 weeks", url: getLicenseUrl("London", "liquor") },
    ],
    "Paris": [
      { name: "Licence IV (Full Alcohol)", estimatedCost: "€5,000–€20,000", processingTime: "2–6 months", url: getLicenseUrl("Paris", "liquor"), warning: "Licence IV permits are capped. Must be purchased from a closing establishment." },
      { name: "Licence III (Beer, Wine, Cider)", estimatedCost: "€800–€2,000", processingTime: "1–3 months", url: getLicenseUrl("Paris", "beer_wine") },
    ],
    "Dubai": [
      { name: "Liquor License (Hotel/Restaurant)", estimatedCost: "AED 20,000–50,000", processingTime: "4–12 weeks", url: getLicenseUrl("Dubai", "liquor"), warning: "Only hotels and certain licensed restaurants can serve alcohol. Strict regulations apply." },
    ],
    "Tokyo": [
      { name: "Liquor License (Inshu)", estimatedCost: "¥30,000–¥150,000", processingTime: "2–3 months", url: getLicenseUrl("Tokyo", "liquor") },
    ],
    "Singapore": [
      { name: "Liquor License (Class 1A/1B)", estimatedCost: "SGD 110–$2,200", processingTime: "2–4 weeks", url: getLicenseUrl("Singapore", "liquor"), warning: "Hours restricted: no alcohol sales 10:30pm–7am in public areas (Liquor Control Zones)." },
    ],
  };

  return {
    general: base,
    alcohol: alcoholLicenses[cityName] || [
      { name: "Alcohol/Liquor License", estimatedCost: "Varies by jurisdiction", processingTime: "1–6 months", url: "#" },
    ],
  };
}

// Cities that have full neighborhood data (lowercase keys matching EXPANDED_NEIGHBORHOODS).
// Used to decide whether `comingSoon` should be removed.
const CITIES_WITH_NEIGHBORHOODS = new Set<string>([
  "new york", "miami", "los angeles", "chicago", "houston", "san francisco", "las vegas",
  "nashville", "austin", "boston", "seattle", "atlanta", "orlando", "dallas", "denver",
  "new orleans", "portland", "phoenix",
  "london", "manchester", "edinburgh", "birmingham", "bristol", "leeds", "liverpool",
  "glasgow", "brighton", "oxford", "cambridge", "bath",
  "paris", "rome", "milan", "florence", "barcelona", "madrid", "valencia", "amsterdam",
  "berlin", "munich", "hamburg", "lisbon", "athens", "vienna", "prague", "dubrovnik",
  "copenhagen", "stockholm", "zurich",
  "dubai", "abu dhabi", "doha", "riyadh", "beirut",
  "singapore", "hong kong", "tokyo", "sydney", "melbourne", "brisbane", "bangkok",
  "bali", "shanghai",
  "toronto", "vancouver", "mexico city", "são paulo", "sao paulo", "buenos aires", "bogotá",
  // Newly added cities — enabled in dropdown (neighborhood data may be added later)
  "san diego",
  "marbella", "ibiza",
  "naples", "venice", "bologna",
  "nice", "bordeaux",
  "geneva", "crans-montana",
  "mykonos", "santorini", "thessaloniki",
  "split",
  "phuket", "chiang mai",
  "gold coast",
  "medellín", "medellin", "cartagena",
  "guadalajara", "monterrey", "tulum",
  "kraków", "krakow",
  "kuala lumpur",
  "seoul",
  "lima",
  "montevideo",
]);

function hasNeighborhoods(cityName: string): boolean {
  return CITIES_WITH_NEIGHBORHOODS.has(cityName.toLowerCase().trim());
}

// Helper to build a city entry. Auto-removes comingSoon when neighborhood data exists.
function city(
  name: string,
  emoji: string,
  country: string,
  costMultiplier: number,
  currency: string,
  currencySymbol: string,
  opts: { state?: string; warnings?: string[]; forceComingSoon?: boolean } = {}
): CityData {
  return {
    name,
    emoji,
    state: opts.state,
    country,
    costMultiplier,
    currency,
    currencySymbol,
    licenses: getCityLicenses(name),
    warnings: opts.warnings,
    comingSoon: opts.forceComingSoon ? true : !hasNeighborhoods(name),
  };
}

export const LOCATION_DATA: CountryData[] = [
  {
    region: "United States",
    country: "United States",
    flag: "🇺🇸",
    placeholder: "e.g. Miami, New York",
    cities: [
      city("New York", "🗽", "United States", 1.40, "USD", "$", { state: "NY", warnings: ["NYC health department letter grading (A/B/C) is mandatory and publicly posted.", "Sidewalk café permit required for outdoor seating."] }),
      city("Miami", "🌴", "United States", 1.30, "USD", "$", { state: "FL", warnings: ["Florida quota liquor licenses are extremely limited and expensive.", "Hurricane-season insurance requirements may increase costs."] }),
      city("Los Angeles", "☀️", "United States", 1.35, "USD", "$", { state: "CA", warnings: ["LA requires seismic retrofit compliance for older buildings."] }),
      city("Chicago", "🍕", "United States", 1.20, "USD", "$", { state: "IL" }),
      city("Houston", "🤠", "United States", 1.00, "USD", "$", { state: "TX" }),
      city("San Francisco", "🌁", "United States", 1.45, "USD", "$", { state: "CA" }),
      city("Las Vegas", "🎰", "United States", 1.00, "USD", "$", { state: "NV" }),
      city("Nashville", "🎸", "United States", 1.05, "USD", "$", { state: "TN" }),
      city("Austin", "🌵", "United States", 1.05, "USD", "$", { state: "TX" }),
      city("Boston", "⚓", "United States", 1.30, "USD", "$", { state: "MA" }),
      city("Seattle", "🌧️", "United States", 1.30, "USD", "$", { state: "WA" }),
      city("Atlanta", "🍑", "United States", 1.00, "USD", "$", { state: "GA" }),
      city("Orlando", "🎢", "United States", 0.95, "USD", "$", { state: "FL" }),
      city("Dallas", "🐎", "United States", 1.00, "USD", "$", { state: "TX" }),
      city("Denver", "🏔️", "United States", 1.05, "USD", "$", { state: "CO" }),
      city("New Orleans", "🎷", "United States", 0.95, "USD", "$", { state: "LA" }),
      city("Portland", "🌲", "United States", 1.10, "USD", "$", { state: "OR" }),
      city("Phoenix", "🌞", "United States", 0.95, "USD", "$", { state: "AZ" }),
      city("San Diego", "🌊", "United States", 1.25, "USD", "$", { state: "CA" }),
    ],
  },
  {
    region: "Latin America",
    country: "Brazil",
    flag: "🇧🇷",
    placeholder: "e.g. São Paulo, Rio de Janeiro",
    cities: [
      city("São Paulo", "🇧🇷", "Brazil", 0.55, "BRL", "R$"),
    ],
  },
  {
    region: "Latin America",
    country: "Mexico",
    flag: "🇲🇽",
    placeholder: "e.g. Mexico City, Cancún",
    cities: [
      city("Mexico City", "🇲🇽", "Mexico", 0.45, "MXN", "$"),
      city("Guadalajara", "🇲🇽", "Mexico", 0.40, "MXN", "$"),
      city("Monterrey", "🇲🇽", "Mexico", 0.45, "MXN", "$"),
      city("Tulum", "🇲🇽", "Mexico", 0.65, "MXN", "$"),
    ],
  },
  {
    region: "Latin America",
    country: "Argentina",
    flag: "🇦🇷",
    placeholder: "e.g. Buenos Aires",
    cities: [
      city("Buenos Aires", "🇦🇷", "Argentina", 0.35, "ARS", "$"),
    ],
  },
  {
    region: "Latin America",
    country: "Colombia",
    flag: "🇨🇴",
    placeholder: "e.g. Bogotá",
    cities: [
      city("Bogotá", "🇨🇴", "Colombia", 0.35, "COP", "$"),
      city("Medellín", "🇨🇴", "Colombia", 0.35, "COP", "$"),
      city("Cartagena", "🇨🇴", "Colombia", 0.40, "COP", "$"),
    ],
  },
  {
    region: "Americas",
    country: "Canada",
    flag: "🇨🇦",
    placeholder: "e.g. Toronto, Vancouver",
    cities: [
      city("Toronto", "🇨🇦", "Canada", 1.10, "CAD", "C$"),
      city("Vancouver", "🇨🇦", "Canada", 1.15, "CAD", "C$"),
    ],
  },
  {
    region: "Europe",
    country: "United Kingdom",
    flag: "🇬🇧",
    placeholder: "e.g. London, Manchester",
    cities: [
      city("London", "🇬🇧", "United Kingdom", 1.35, "GBP", "£", { warnings: ["Premises licence application requires public consultation period."] }),
      city("Manchester", "🇬🇧", "United Kingdom", 0.95, "GBP", "£"),
      city("Edinburgh", "🇬🇧", "United Kingdom", 1.00, "GBP", "£"),
      city("Birmingham", "🇬🇧", "United Kingdom", 0.90, "GBP", "£"),
      city("Bristol", "🇬🇧", "United Kingdom", 0.95, "GBP", "£"),
      city("Leeds", "🇬🇧", "United Kingdom", 0.85, "GBP", "£"),
      city("Liverpool", "🇬🇧", "United Kingdom", 0.85, "GBP", "£"),
      city("Glasgow", "🇬🇧", "United Kingdom", 0.85, "GBP", "£"),
      city("Brighton", "🇬🇧", "United Kingdom", 1.05, "GBP", "£"),
      city("Oxford", "🇬🇧", "United Kingdom", 1.10, "GBP", "£"),
      city("Cambridge", "🇬🇧", "United Kingdom", 1.10, "GBP", "£"),
      city("Bath", "🇬🇧", "United Kingdom", 1.00, "GBP", "£"),
    ],
  },
  {
    region: "Europe",
    country: "France",
    flag: "🇫🇷",
    placeholder: "e.g. Paris, Lyon",
    cities: [
      city("Paris", "🇫🇷", "France", 1.30, "EUR", "€", { warnings: ["Licence IV (full alcohol) is capped and must be purchased from a closing venue."] }),
      city("Nice", "🇫🇷", "France", 1.10, "EUR", "€"),
      city("Bordeaux", "🇫🇷", "France", 1.00, "EUR", "€"),
    ],
  },
  {
    region: "Europe",
    country: "Italy",
    flag: "🇮🇹",
    placeholder: "e.g. Milan, Rome",
    cities: [
      city("Milan", "🇮🇹", "Italy", 1.15, "EUR", "€"),
      city("Rome", "🇮🇹", "Italy", 1.10, "EUR", "€"),
      city("Florence", "🇮🇹", "Italy", 1.05, "EUR", "€"),
      city("Naples", "🇮🇹", "Italy", 0.90, "EUR", "€"),
      city("Venice", "🇮🇹", "Italy", 1.15, "EUR", "€"),
      city("Bologna", "🇮🇹", "Italy", 1.00, "EUR", "€"),
    ],
  },
  {
    region: "Europe",
    country: "Spain",
    flag: "🇪🇸",
    placeholder: "e.g. Barcelona, Madrid",
    cities: [
      city("Barcelona", "🇪🇸", "Spain", 1.05, "EUR", "€"),
      city("Madrid", "🇪🇸", "Spain", 1.05, "EUR", "€"),
      city("Valencia", "🇪🇸", "Spain", 0.85, "EUR", "€"),
      city("Marbella", "🇪🇸", "Spain", 1.10, "EUR", "€"),
      city("Ibiza", "🇪🇸", "Spain", 1.20, "EUR", "€"),
    ],
  },
  {
    region: "Europe",
    country: "Netherlands",
    flag: "🇳🇱",
    placeholder: "e.g. Amsterdam",
    cities: [
      city("Amsterdam", "🇳🇱", "Netherlands", 1.20, "EUR", "€"),
    ],
  },
  {
    region: "Europe",
    country: "Germany",
    flag: "🇩🇪",
    placeholder: "e.g. Berlin, Munich",
    cities: [
      city("Berlin", "🇩🇪", "Germany", 1.05, "EUR", "€"),
      city("Munich", "🇩🇪", "Germany", 1.20, "EUR", "€"),
      city("Hamburg", "🇩🇪", "Germany", 1.10, "EUR", "€"),
    ],
  },
  {
    region: "Europe",
    country: "Portugal",
    flag: "🇵🇹",
    placeholder: "e.g. Lisbon",
    cities: [
      city("Lisbon", "🇵🇹", "Portugal", 0.85, "EUR", "€"),
    ],
  },
  {
    region: "Europe",
    country: "Greece",
    flag: "🇬🇷",
    placeholder: "e.g. Athens",
    cities: [
      city("Athens", "🇬🇷", "Greece", 0.80, "EUR", "€"),
      city("Mykonos", "🇬🇷", "Greece", 1.20, "EUR", "€"),
      city("Santorini", "🇬🇷", "Greece", 1.15, "EUR", "€"),
      city("Thessaloniki", "🇬🇷", "Greece", 0.75, "EUR", "€"),
    ],
  },
  {
    region: "Europe",
    country: "Austria",
    flag: "🇦🇹",
    placeholder: "e.g. Vienna",
    cities: [
      city("Vienna", "🇦🇹", "Austria", 1.10, "EUR", "€"),
    ],
  },
  {
    region: "Europe",
    country: "Czech Republic",
    flag: "🇨🇿",
    placeholder: "e.g. Prague",
    cities: [
      city("Prague", "🇨🇿", "Czech Republic", 0.75, "CZK", "Kč"),
    ],
  },
  {
    region: "Europe",
    country: "Croatia",
    flag: "🇭🇷",
    placeholder: "e.g. Dubrovnik",
    cities: [
      city("Dubrovnik", "🇭🇷", "Croatia", 0.90, "EUR", "€"),
      city("Split", "🇭🇷", "Croatia", 0.85, "EUR", "€"),
    ],
  },
  {
    region: "Europe",
    country: "Denmark",
    flag: "🇩🇰",
    placeholder: "e.g. Copenhagen",
    cities: [
      city("Copenhagen", "🇩🇰", "Denmark", 1.30, "DKK", "kr"),
    ],
  },
  {
    region: "Europe",
    country: "Sweden",
    flag: "🇸🇪",
    placeholder: "e.g. Stockholm",
    cities: [
      city("Stockholm", "🇸🇪", "Sweden", 1.20, "SEK", "kr"),
    ],
  },
  {
    region: "Europe",
    country: "Switzerland",
    flag: "🇨🇭",
    placeholder: "e.g. Zurich",
    cities: [
      city("Zurich", "🇨🇭", "Switzerland", 1.60, "CHF", "CHF"),
      city("Geneva", "🇨🇭", "Switzerland", 1.60, "CHF", "CHF"),
      city("Crans-Montana", "🇨🇭", "Switzerland", 1.50, "CHF", "CHF"),
    ],
  },
  {
    region: "Middle East",
    country: "United Arab Emirates",
    flag: "🇦🇪",
    placeholder: "e.g. Dubai, Abu Dhabi",
    cities: [
      city("Dubai", "🇦🇪", "United Arab Emirates", 1.30, "AED", "AED", { warnings: ["Only hotels and licensed restaurants may serve alcohol.", "Trade license from DED required for all F&B businesses."] }),
      city("Abu Dhabi", "🇦🇪", "United Arab Emirates", 1.25, "AED", "AED"),
    ],
  },
  {
    region: "Middle East",
    country: "Qatar",
    flag: "🇶🇦",
    placeholder: "e.g. Doha",
    cities: [
      city("Doha", "🇶🇦", "Qatar", 1.20, "QAR", "QAR"),
    ],
  },
  {
    region: "Middle East",
    country: "Saudi Arabia",
    flag: "🇸🇦",
    placeholder: "e.g. Riyadh",
    cities: [
      city("Riyadh", "🇸🇦", "Saudi Arabia", 1.05, "SAR", "SAR"),
    ],
  },
  {
    region: "Middle East",
    country: "Lebanon",
    flag: "🇱🇧",
    placeholder: "e.g. Beirut",
    cities: [
      city("Beirut", "🇱🇧", "Lebanon", 0.70, "USD", "$"),
    ],
  },
  {
    region: "Asia",
    country: "Japan",
    flag: "🇯🇵",
    placeholder: "e.g. Tokyo, Osaka",
    cities: [
      city("Tokyo", "🇯🇵", "Japan", 1.25, "JPY", "¥"),
    ],
  },
  {
    region: "Asia",
    country: "Singapore",
    flag: "🇸🇬",
    placeholder: "e.g. Singapore",
    cities: [
      city("Singapore", "🇸🇬", "Singapore", 1.35, "SGD", "S$", { warnings: ["Liquor Control Zones restrict alcohol sale hours (10:30pm–7am)."] }),
    ],
  },
  {
    region: "Asia",
    country: "Hong Kong",
    flag: "🇭🇰",
    placeholder: "e.g. Hong Kong",
    cities: [
      city("Hong Kong", "🇭🇰", "Hong Kong", 1.40, "HKD", "HK$"),
    ],
  },
  {
    region: "Asia",
    country: "Thailand",
    flag: "🇹🇭",
    placeholder: "e.g. Bangkok",
    cities: [
      city("Bangkok", "🇹🇭", "Thailand", 0.50, "THB", "฿"),
      city("Phuket", "🇹🇭", "Thailand", 0.55, "THB", "฿"),
      city("Chiang Mai", "🇹🇭", "Thailand", 0.40, "THB", "฿"),
    ],
  },
  {
    region: "Asia",
    country: "Indonesia",
    flag: "🇮🇩",
    placeholder: "e.g. Bali",
    cities: [
      city("Bali", "🇮🇩", "Indonesia", 0.45, "IDR", "Rp"),
    ],
  },
  {
    region: "Asia",
    country: "China",
    flag: "🇨🇳",
    placeholder: "e.g. Shanghai, Beijing",
    cities: [
      city("Shanghai", "🇨🇳", "China", 1.10, "CNY", "¥"),
    ],
  },
  {
    region: "Asia Pacific",
    country: "Australia",
    flag: "🇦🇺",
    placeholder: "e.g. Sydney, Melbourne",
    cities: [
      city("Sydney", "🇦🇺", "Australia", 1.25, "AUD", "A$"),
      city("Melbourne", "🇦🇺", "Australia", 1.20, "AUD", "A$"),
      city("Brisbane", "🇦🇺", "Australia", 1.10, "AUD", "A$"),
      city("Gold Coast", "🇦🇺", "Australia", 1.10, "AUD", "A$"),
    ],
  },
  {
    region: "Europe",
    country: "Poland",
    flag: "🇵🇱",
    placeholder: "e.g. Kraków",
    cities: [
      city("Kraków", "🇵🇱", "Poland", 0.65, "PLN", "zł"),
    ],
  },
  {
    region: "Asia",
    country: "Malaysia",
    flag: "🇲🇾",
    placeholder: "e.g. Kuala Lumpur",
    cities: [
      city("Kuala Lumpur", "🇲🇾", "Malaysia", 0.55, "MYR", "RM"),
    ],
  },
  {
    region: "Asia",
    country: "South Korea",
    flag: "🇰🇷",
    placeholder: "e.g. Seoul",
    cities: [
      city("Seoul", "🇰🇷", "South Korea", 1.15, "KRW", "₩"),
    ],
  },
  {
    region: "Latin America",
    country: "Peru",
    flag: "🇵🇪",
    placeholder: "e.g. Lima",
    cities: [
      city("Lima", "🇵🇪", "Peru", 0.45, "PEN", "S/"),
    ],
  },
  {
    region: "Latin America",
    country: "Uruguay",
    flag: "🇺🇾",
    placeholder: "e.g. Montevideo",
    cities: [
      city("Montevideo", "🇺🇾", "Uruguay", 0.70, "UYU", "$U"),
    ],
  },
];

// Helpers
export function getAllRegions(): string[] {
  const regions = new Set(LOCATION_DATA.map(c => c.region));
  return Array.from(regions);
}

export function getCountriesByRegion(region: string): CountryData[] {
  return LOCATION_DATA.filter(c => c.region === region);
}

export function getAllCountries(): CountryData[] {
  return LOCATION_DATA;
}

export function getCitiesForCountry(countryName: string): CityData[] {
  const country = LOCATION_DATA.find(c => c.country === countryName);
  return country?.cities || [];
}

export function getCityData(cityName: string): CityData | undefined {
  for (const country of LOCATION_DATA) {
    const city = country.cities.find(c => c.name === cityName);
    if (city) return city;
  }
  return undefined;
}

export function getPlaceholderForCountry(countryName: string): string {
  const country = LOCATION_DATA.find(c => c.country === countryName);
  return country?.placeholder || "Select a city";
}
